import { useCallback } from 'react';
import uuid from 'react-uuid';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';
import { decodePriority } from 'src/utils/messageHelpers';

export interface Message {
  messageId: number;
  clientId?: string;
  subject: string;
  message: string;
  status: string;
  decodedStatus: string;  
  isDeleted: boolean;
  isDeletedMsgCast?: boolean;
  createdBy: string;
  createdByUserName: string;
  creationDate?: string | Date | null;
  creationDateOnly?: Date;
  lastUpdatedBy: string;
  lastUpdateDate: string;
  projectId: number | null;
  priority: number | string;
  decodedPriority?: string;
  attachmentsCount: number;
  recipient?: string;
  contactFullName?: string;
  contactPhone?: string;
  rowId?: string;
  selected?: boolean;
  isNewMessage?: boolean;
  recipientCompanyName?: string;
}

export type MessageBoxType = 'inbox' | 'outbox';

interface UseGetMessagesGenericOptions {
  clientId: string;
  boxType: MessageBoxType;
  sortColumn?: string;
}

const useGetMessagesGeneric = (options: UseGetMessagesGenericOptions) => {
  const { clientId, boxType, sortColumn } = options;

  const call = useCallback(async () => {
    try {
      if (!clientId || clientId === '') {
        return [];
      }

      const actualSortColumn = sortColumn || 'creationDate';
      const endpoint = boxType === 'inbox' ? 'Message/inbox' : 'Message/outbox';
      
      const res = await axiosInstance.get(endpoint, {
        params: { clientId, sortColumn: actualSortColumn },
      });

      const data: Message[] = (res.data?.messages || []).map((item: any) => {
        // Create date-only field for filtering and convert creationDate to Date object for sorting
        const creationDate = item.creationDate ? new Date(item.creationDate) : null;
        const creationDateOnly =
          creationDate && !isNaN(creationDate.getTime())
            ? new Date(
                creationDate.getFullYear(),
                creationDate.getMonth(),
                creationDate.getDate()
              )
            : undefined;

        // Map fields differently based on inbox vs outbox
        let contactFullName: string;
        let contactPhone: string;
        let createdByUserName: string;

        if (boxType === 'inbox') {
          // Inbox: fullName and telecomNumber1 are sender info
          const fullName = item.fullName;
          const telecomNumber1 = item.telecomNumber1;

          contactFullName = fullName || '';
          contactPhone = telecomNumber1 || '';
          
          // Try multiple potential fields for the sender name in inbox messages
          createdByUserName =
            item.createdByUserName ??
            item.senderName ??
            item.fromUserName ??
            item.fromUser ??
            item.sender ??
            item.authorName ??
            fullName ??
            'Unknown Sender';
        } else {
          // Outbox: Look for recipient-specific fields
          const senderFullName = item.fullName;
          
          // Look for recipient contact info in various possible fields
          const recipientTelecomNumber =
            item.recipientPhone ||
            item.toPhone ||
            item.recipientTelecom ||
            item.toTelecom ||
            item.telecomNumber1 ||
            '';
          const recipientFullName =
            item.recipientFullName ||
            item.recipient ||
            item.toFullName ||
            item.toUserName ||
            item.toName ||
            '';

          contactFullName = recipientFullName;
          contactPhone = recipientTelecomNumber;
          createdByUserName =
            item.createdByUserName ?? item.senderName ?? senderFullName ?? 'Unknown Sender';
        }

        const mappedItem: Message = {
          messageId: item.messageId ?? 0,
          clientId: item.clientId ?? clientId,
          subject: item.subject ?? '',
          message: item.message ?? item.body ?? '',
          status: String(item.status ?? ''),
          decodedStatus: item.decodedStatus ?? '',
          //timestamp: timestampDate,
          //timestampDateOnly: timestampDateOnly,
          isDeleted:
            item.isDeleted === true ||
            item.isDeleted === 'Yes' ||
            item.isDeleted === 'yes' ||
            item.isDeleted === 'True' ||
            item.isDeleted === 'true',
          isDeletedMsgCast:
            item.isDeletedMsgCast === true ||
            item.isDeletedMsgCast === 'Yes' ||
            item.isDeletedMsgCast === 'yes' ||
            item.isDeletedMsgCast === 'True' ||
            item.isDeletedMsgCast === 'true',
          createdBy: item.createdBy ?? '',
          createdByUserName,
          creationDate: creationDate,
          creationDateOnly: creationDateOnly,
          lastUpdatedBy: item.lastUpdatedBy ?? '',
          lastUpdateDate: item.lastUpdateDate ?? '',
          projectId: item.projectId ?? null,
          priority: item.priority ?? '',
          decodedPriority:
            boxType === 'inbox'
              ? decodePriority(item.priority, item.decodedPriority)
              : item.decodedPriority ?? '',
          attachmentsCount: item.attachmentsCount ?? 0,
          recipient: boxType === 'outbox' ? contactFullName : (item.recipient ?? ''),
          contactFullName,
          contactPhone,
          rowId: item.rowId ?? `msg_${item.messageId}_${uuid()}`,
          selected: item.selected ?? false,
          isNewMessage: item.isNewMessage ?? false,
          recipientCompanyName: item.clientCompanyName ?? '',
        };

        return mappedItem;
      });

      return data;
    } catch (error: any) {
      let apiMessage = '';
      if (error.response) {
        apiMessage = `Status: ${error.response.status}, Message: ${
          error.response.data?.message || error.response.statusText
        }`;
      } else {
        apiMessage = error.message || String(error);
      }
      
      console.error(`Get ${boxType} messages API error:`, apiMessage);
      console.error('Error details:', {
        endpoint: `Message/${boxType}`,
        clientId,
        sortColumn,
        error,
      });
      
      throw new Error(apiMessage);
    }
  }, [clientId, boxType, sortColumn]);

  const { execute, value, isLoading, error } = useAsync(call, false, undefined, []);

  return {
    getMessages: execute,
    messages: value || [],
    isMessagesLoading: isLoading,
    messagesError: error,
  };
};

export default useGetMessagesGeneric;

// Shared delete messages function
export const deleteMessages = async (
  clientId: string,
  messageIds: number[],
  isDelete: boolean
) => {
  try {
    return await axiosInstance.patch(`Message/DeleteMessages/${clientId}`, {
      messageIds,
      isDelete,
    });
  } catch (error) {
    console.error('Delete messages API error:', error);
    throw error;
  }
};
